"use client";

import Accordion from "@/components/Accordion";
import ExpandableTextSection from "@/components/ExpandableTextSection";
import { JsonLdScript } from "@/components/JsonLdScript";
import DebugDataDisplay from "@/components/pageBuilder/partials/DebugDataDisplay";
import LayoutWrapper from "@/components/pageBuilder/partials/LayoutWrapper";
import ShowAllButton from "@/components/pageBuilder/partials/ShowAllButton";
import ViewEventTracker from "@/components/pageBuilder/partials/ViewEventTracker";
import ViewPromotionTracker from "@/components/pageBuilder/partials/ViewPromotionTracker";
import Swiper from "@/components/swiper";
import TabSwitcher from "@/components/TabSwitcher";
import TurnaBackgroundLogo, { TurnaLogoIcon } from "@/components/TurnaLogo";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useTranslations } from "next-intl";
import { DISPLAY_TYPES } from "@/types/displayTypes";
import { resolveI18nConfig } from "@/utils/i18nConfigHelper";
import { generateFaqSchema } from "@/utils/schemaUtils";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { SwiperSlide } from "swiper/react";

/**
 * PageBuilder Component - Data Display Version
 * Config dosyasından gelen section'ları sadece data olarak gösteren component
 */
export default function PageBuilder({
  config,
  locale,
  currency,
  additionalParams = {},
  componentRegistry = {},
  itemPropMapping = {},
  translationData = {},
  hideEmptySections = false,
}) {
  if (!config || !config.sections) {
    console.error("PageBuilder: Config veya sections bulunamadı");
    return null;
  }

  // Section'ları config'deki sıraya göre göster (priority sıralaması yok)
  return (
    <>
      {config.sections.map((section, index) => (
        <DataDisplaySection
          key={section.id + index}
          section={section}
          locale={locale}
          currency={currency}
          additionalParams={additionalParams}
          componentRegistry={componentRegistry}
          itemPropMapping={itemPropMapping}
          translationData={translationData}
          hideEmptySections={hideEmptySections}
        />
      ))}
    </>
  );
}

/**
 * DataDisplaySection Component
 * Her bir section'ın data'sını <pre> tag ile gösterir
 */
function DataDisplaySection({
  section,
  locale,
  currency,
  additionalParams,
  componentRegistry,
  itemPropMapping,
  translationData,
  hideEmptySections = false,
}) {
  const { id, type, priority = "medium" } = section;

  // i18n desteği için translation hook'u
  //const t = useTranslationDetector();
  const t = useTranslations();

  // i18n key'lerini çözümle
  const resolvedSection = resolveI18nConfig(section, t, translationData);
  const { title, description, link } = resolvedSection;

  // Data'yı al
  const data = getSectionData(section.source, {
    locale,
    currency,
    ...additionalParams,
  });

  // Component registry ve item prop eşleme tablosu
  // Registry tamamen dışarıdan gelir; bulunamazsa render etme
  const COMPONENT_REGISTRY = componentRegistry || {};
  const ITEM_PROP_MAPPING = itemPropMapping || {};

  // Hook'ları component'in en üstünde tanımla (React hooks rules)
  const defaultActiveIndex =
    (section.displayType === DISPLAY_TYPES.TAB ||
      section.displayType === DISPLAY_TYPES.TAB_SWIPER) &&
      resolvedSection.settings?.tab?.defaultActiveIndex
      ? Number.isInteger(resolvedSection.settings.tab.defaultActiveIndex)
        ? resolvedSection.settings.tab.defaultActiveIndex
        : 0
      : 0;

  const [activeTab, setActiveTab] = useState(defaultActiveIndex);

  // displayLimit için showAll state'i (her section için ayrı, section.id ile key'lenir)
  const [showAllStates, setShowAllStates] = useState({});

  const showAll = showAllStates[section.id] || false;
  const setShowAll = (value) => {
    setShowAllStates((prev) => ({
      ...prev,
      [section.id]: typeof value === "function" ? value(prev[section.id] || false) : value,
    }));
  };

  // Sadece verisi olan tab'ları filtrele (boş tab'lar gösterilmez)
  const tabsWithData = useMemo(() => {
    if (
      section.displayType !== DISPLAY_TYPES.TAB &&
      section.displayType !== DISPLAY_TYPES.TAB_SWIPER
    )
      return [];

    const tabSettings = resolvedSection.settings?.tab || {};
    const tabs = Array.isArray(tabSettings.tabs) ? tabSettings.tabs : [];
    const sourceIsArray = Array.isArray(data);
    const sourceIsObject =
      data && typeof data === "object" && !Array.isArray(data);

    if (!tabs.length) return [];

    return tabs.filter((tab) => {
      // path tab'ı: data objesindeki alan array ve dolu mu?
      if (tab.path && sourceIsObject) {
        const arr = data?.[tab.path];
        return Array.isArray(arr) && arr.length > 0;
      }
      // value === 'all'
      if (tab.value === "all") {
        if (sourceIsArray) return data.length > 0;
        if (sourceIsObject) {
          return Object.values(data).some(
            (v) => Array.isArray(v) && v.length > 0
          );
        }
        return false;
      }
      // field tab'ı (array kaynak)
      if (tab.field && sourceIsArray) {
        const expected = tab.value;
        const operator = tab.operator || "eq";
        const filtered = data.filter((item) => {
          const actual = item?.[tab.field];
          switch (operator) {
            case "eq":
              return actual === expected;
            case "ne":
              return actual !== expected;
            case "in":
              return Array.isArray(expected) ? expected.includes(actual) : false;
            case "truthy":
              return Boolean(actual);
            case "falsy":
              return !actual;
            default:
              return actual === expected;
          }
        });
        return filtered.length > 0;
      }
      return false;
    });
  }, [data, section.displayType, resolvedSection.settings?.tab]);

  // Tab listesi değiştiğinde activeTab'ı geçerli aralıkta tut
  useEffect(() => {
    if (tabsWithData.length > 0 && activeTab >= tabsWithData.length) {
      setActiveTab(0);
    }
  }, [tabsWithData.length, activeTab]);

  const filteredItems = useMemo(() => {
    if (
      section.displayType !== DISPLAY_TYPES.TAB &&
      section.displayType !== DISPLAY_TYPES.TAB_SWIPER
    )
      return [];

    const sourceIsArray = Array.isArray(data);
    const sourceIsObject =
      data && typeof data === "object" && !Array.isArray(data);

    if (!tabsWithData.length) {
      if (sourceIsArray) return data;
      if (sourceIsObject) {
        return Object.values(data).flatMap((v) => (Array.isArray(v) ? v : []));
      }
      return [];
    }

    const current = tabsWithData[activeTab] || tabsWithData[0];
    if (!current) return [];

    // Path tab'ı: data objesindeki belirli alandan oku
    if (current.path && sourceIsObject) {
      const valueAtPath = data?.[current.path];
      return Array.isArray(valueAtPath) ? valueAtPath : [];
    }

    // value === 'all' -> tümünü göster
    if (current.value === "all") {
      if (sourceIsArray) return data;
      if (sourceIsObject) {
        return Object.values(data).flatMap((v) => (Array.isArray(v) ? v : []));
      }
      return [];
    }

    // Alan bazlı filtre (array kaynak için)
    if (sourceIsArray) {
      const field = current.field;
      const expected = current.value;
      const operator = current.operator || "eq";
      if (!field) return data;
      return data.filter((item) => {
        const actual = item?.[field];
        switch (operator) {
          case "eq":
            return actual === expected;
          case "ne":
            return actual !== expected;
          case "in":
            return Array.isArray(expected) ? expected.includes(actual) : false;
          case "truthy":
            return Boolean(actual);
          case "falsy":
            return !actual;
          default:
            return actual === expected;
        }
      });
    }

    return [];
  }, [data, activeTab, section.displayType, tabsWithData]);

  const uiTabs = useMemo(() => {
    return tabsWithData.map((t, index) => ({ id: index, label: t.label }));
  }, [tabsWithData]);

  // displayType: "component" → doğrudan bir component render et
  if (section.displayType === DISPLAY_TYPES.COMPONENT && section.component) {
    const shouldCheckData = section.requiresData !== false;
    const componentExpectsData = section.source != null;
    if (
      hideEmptySections &&
      shouldCheckData &&
      componentExpectsData &&
      !hasMeaningfulData(data)
    ) {
      return null;
    }

    const compName = section.component;
    // Önce dışarıdan registry'e bak
    let DirectComp = COMPONENT_REGISTRY[compName];

    if (!DirectComp) return null;

    // Component'e geçilecek prop'ları hazırla
    let componentProps = {
      ...(section.componentProps || {}),
    };

    // Source data'yı prop olarak ekle (config'den ayarlanabilir)
    if (data !== null && data !== undefined) {
      if (section.dataProp) {
        // Config'de belirtilen prop adıyla data'yı geç
        componentProps[section.dataProp] = data;
      } else {
        // Tüm data'yı componentProps'a ekle
        componentProps = { ...componentProps, ...data };
      }
    }

    // Ekstra data prop'ları (opsiyonel)
    if (section.additionalDataProps) {
      Object.keys(section.additionalDataProps).forEach((propName) => {
        const dataPath = section.additionalDataProps[propName];
        if (dataPath && data && typeof data === "object") {
          // Nested property access (örn: "items.0.title")
          const value = dataPath
            .split(".")
            .reduce((obj, key) => obj?.[key], data);
          if (value !== undefined) {
            componentProps[propName] = value;
          }
        }
      });
    }

    const componentBlock = (
      <LayoutWrapper
        title={title}
        description={description}
        link={link}
        settings={resolvedSection.settings}
      >
        <DirectComp {...componentProps} />
      </LayoutWrapper>
    );

    const componentContent = section.dataLayer?.allEvents ? (
      <ViewEventTracker events={section.dataLayer.allEvents}>
        {componentBlock}
      </ViewEventTracker>
    ) : componentBlock;

    return (
      <>
        {componentContent}

        {/* Debug Data Display */}
        <DebugDataDisplay
          sectionId={id}
          sectionType={type}
          data={data}
          source={section.source}
          additionalInfo={{
            displayType: section.displayType,
            component: section.component,
            componentProps: componentProps,
            dataProp: section.dataProp,
            additionalDataProps: section.additionalDataProps,
            priority: priority,
            locale,
            currency,
            additionalParams,
          }}
        />
      </>
    );
  }

  // displayType: "list" ve cardComponent tanımlı ise liste render et
  if (
    section.displayType === DISPLAY_TYPES.LIST &&
    section.cardComponent &&
    Array.isArray(data)
  ) {
    if (hideEmptySections && data.length === 0) return null;
    const Card = COMPONENT_REGISTRY[section.cardComponent];
    const itemPropName = ITEM_PROP_MAPPING[section.cardComponent] || "";

    if (Card) {
      const listSettings = resolvedSection.settings?.list || {};
      const listContainerClass = listSettings.customClass || "";
      const displayLimit = listSettings.displayLimit;

      // displayLimit varsa ilk N item'ı göster
      const shouldShowButton = displayLimit && data.length > displayLimit;
      const displayedItems = displayLimit && !showAll
        ? data.slice(0, displayLimit)
        : data;

      // Card'lara geçilecek ekstra prop'lar
      const cardAdditionalProps = section.cardAdditionalProps || {};

      const listBlock = (
        <LayoutWrapper
          title={title}
          description={description}
          link={link}
          settings={resolvedSection.settings}
        >
          <div className={clsx("list-container", listContainerClass)}>
            {displayedItems.map((item, index) => {

              // dataLayer mappings data
              const dataLayerPropName = section.dataLayer?.itemPropName || "";
              const dataLayerItem = dataLayerPropName ? item[dataLayerPropName] : null;

              const key = getStableItemKey(item, index, section.id);
              const props = itemPropName
                ? {
                  [itemPropName]: item,
                  ...cardAdditionalProps, // Ekstra prop'ları ekle
                }
                : {
                  ...item, // itemPropName yoksa item'ı direkt spread et
                  ...cardAdditionalProps, // Ekstra prop'ları ekle
                };

              if (dataLayerItem && dataLayerPropName != "") {
                props[dataLayerPropName] = dataLayerItem;
              }

              return <Card key={key} {...props} />;
            })}
          </div>
          {shouldShowButton && (
            <ShowAllButton
              isExpanded={showAll}
              onToggle={() => setShowAll(!showAll)}
            />
          )}
        </LayoutWrapper>
      );

      const listContent = section.dataLayer?.allEvents ? (
        <ViewEventTracker events={section.dataLayer.allEvents}>
          {listBlock}
        </ViewEventTracker>
      ) : listBlock;

      return (
        <>
          {listContent}

          {/* Debug Data Display */}
          <DebugDataDisplay
            sectionId={id}
            sectionType={type}
            data={data}
            source={section.source}
            additionalInfo={{
              displayType: section.displayType,
              cardComponent: section.cardComponent,
              itemPropName,
              cardAdditionalProps,
              listSettings,
              priority: priority,
              locale,
              currency,
              additionalParams,
            }}
          />
        </>
      );
    }

    // Card bulunamazsa hiç render etme
    return null;
  }

  // displayType: "swiper" ve cardComponent tanımlı ise swiper render et
  if (
    section.displayType === DISPLAY_TYPES.SWIPER &&
    section.cardComponent &&
    Array.isArray(data)
  ) {
    if (hideEmptySections && data.length === 0) return null;
    const Card = COMPONENT_REGISTRY[section.cardComponent];
    const itemPropName = ITEM_PROP_MAPPING[section.cardComponent] || "item";

    if (Card) {
      // Config'den swiper options'ları al, yoksa default değerleri kullan
      const defaultSwiperOptions = {
        slidesPerView: "auto",
      };

      // Yeni settings yapısından swiper ayarlarını al
      const swiperSettings = resolvedSection.settings?.swiper || {};
      const swiperOptions = {
        ...defaultSwiperOptions,
        ...swiperSettings.options,
      };

      // Card'lara geçilecek ekstra prop'lar
      let cardAdditionalProps = section.cardAdditionalProps || {};

      const swiperBlock = (
        <LayoutWrapper
          title={title}
          description={description}
          link={link}
          settings={resolvedSection.settings}
        >
          <Swiper
            customClass={swiperSettings.customClass}
            swiperOptions={swiperOptions}
          >
            {data.map((item, index) => {

              // dataLayer mappings data
              const dataLayerPropName = section.dataLayer?.itemPropName || "";
              const dataLayerItem = dataLayerPropName ? item[dataLayerPropName] : null;

              // 
              const key = getStableItemKey(item, index, section.id);
              const props = itemPropName
                ? {
                  [itemPropName]: item,
                  ...cardAdditionalProps
                }
                : {
                  ...item,
                  ...cardAdditionalProps,
                };

                if (dataLayerItem && dataLayerPropName != "") {
                  props[dataLayerPropName] = dataLayerItem;
                }

              return (
                <SwiperSlide
                  key={key}
                  className={clsx(
                    swiperSettings.slideClass,
                    index > 0 && swiperSettings.spacingClass,
                  )}
                >
                  <Card {...props} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </LayoutWrapper>
      );

      const swiperContent = section.dataLayer?.allEvents ? (
        <ViewEventTracker events={section.dataLayer.allEvents}>
          {swiperBlock}
        </ViewEventTracker>
      ) : swiperBlock;

      return (
        <>
          {swiperContent}

          {/* Debug Data Display */}
          <DebugDataDisplay
            sectionId={id}
            sectionType={type}
            data={data}
            source={section.source}
            additionalInfo={{
              displayType: section.displayType,
              cardComponent: section.cardComponent,
              itemPropName,
              cardAdditionalProps,
              swiperSettings,
              swiperOptions,
              priority: priority,
              locale,
              currency,
              additionalParams,
            }}
          />
        </>
      );
    }

    // Card bulunamazsa hiç render etme
    return null;
  }

  // displayType: "faq" ise yeni Accordion render et (FaqAccordion yerine)
  if (section.displayType === DISPLAY_TYPES.FAQ) {
    const items = Array.isArray(data) ? data : [];
    if (hideEmptySections && items.length === 0) return null;
    const faqSettings = resolvedSection.settings?.faq || {};
    const customTitle = faqSettings.customTitle || title;
    const singleOpen = !!faqSettings.singleOpen; // true: bir item açılınca diğeri kapanır
    const allowCollapse = faqSettings.allowCollapse !== false; // false: açık olana tıklayınca kapanmasın
    const faqSchema = resolvedSection.settings?.faqSchema; // FAQ schema oluştur

    // FAQ schema oluştur
    const faqSchemaData = faqSchema ? generateFaqSchema(items) : null;

    return (
      <>
        {/* FAQ JSON-LD Schema */}
        {faqSchemaData && (
          <JsonLdScript
            id={`faq-schema-${section.id}`}
            jsonLd={faqSchemaData}
          />
        )}

        <LayoutWrapper
          title={customTitle}
          description={description}
          link={link}
          settings={resolvedSection.settings}
        >
          <Accordion
            items={items}
            singleOpen={singleOpen}
            allowCollapse={allowCollapse}
          />
        </LayoutWrapper>

        {/* Debug Data Display */}
        <DebugDataDisplay
          sectionId={id}
          sectionType={type}
          data={data}
          source={section.source}
          additionalInfo={{
            displayType: section.displayType,
            customTitle,
            singleOpen,
            allowCollapse,
            faqSettings,
            itemsCount: items.length,
            priority: priority,
            locale,
            currency,
            additionalParams,
          }}
        />
      </>
    );
  }

  // displayType: "expandable-text" → ExpandableTextSection + opsiyonel TurnaBackgroundLogo render et
  if (section.displayType === DISPLAY_TYPES.EXPANDABLE_TEXT) {
    const hasContent =
      data?.title || data?.Title || data?.text || data?.Content;
    if (hideEmptySections && !hasContent) return null;
    const exSettings = resolvedSection.settings?.expandable || {};
    const contentClassName = exSettings.contentClassName || "";
    const showTurnaLogo = Boolean(
      data?.showTurnaLogo || exSettings.showTurnaLogo || false,
    );
    const turnaLogoType = exSettings.turnaLogoType || "background"; // background or icon
    const turnaLogoClassName = exSettings.turnaLogoClassName;
    const turnaLogoWidth = exSettings.turnaLogoWidth;
    const turnaLogoHeight = exSettings.turnaLogoHeight;

    return (
      <>
        <LayoutWrapper
          title={data?.title || data?.Title || title}
          description={description}
          link={link}
          settings={resolvedSection.settings}
        >
          <div className={contentClassName}>
            <ExpandableTextSection
              title={data?.title || data?.Title || title}
              text={data?.text || data?.Content || ""}
              showHeader={false}
            />
            {showTurnaLogo && (
              <>
                {turnaLogoType === "background" && (
                  <TurnaBackgroundLogo
                    className={turnaLogoClassName}
                    width={turnaLogoWidth}
                    height={turnaLogoHeight}
                  />
                )}
                {turnaLogoType === "icon" && (
                  <TurnaLogoIcon className={turnaLogoClassName} width={turnaLogoWidth} height={turnaLogoHeight} />)}
              </>
            )}
          </div>
        </LayoutWrapper>

        {/* Debug Data Display */}
        <DebugDataDisplay
          sectionId={id}
          sectionType={type}
          data={data}
          source={section.source}
          additionalInfo={{
            displayType: section.displayType,
            contentClassName,
            showTurnaLogo,
            turnaLogoClassName,
            turnaLogoWidth,
            turnaLogoHeight,
            exSettings,
            priority: priority,
            locale,
            currency,
            additionalParams,
          }}
        />
      </>
    );
  }

  // displayType: "tab" ve cardComponent tanımlı ise tablı liste render et
  if (
    section.displayType === DISPLAY_TYPES.TAB &&
    section.cardComponent &&
    (Array.isArray(data) || (data && typeof data === "object"))
  ) {
    if (hideEmptySections && filteredItems.length === 0) return null;
    const Card = COMPONENT_REGISTRY[section.cardComponent];
    const itemPropName = ITEM_PROP_MAPPING[section.cardComponent] || "";

    if (Card) {
      const tabSettings = resolvedSection.settings?.tab || {};
      const tabs = Array.isArray(tabSettings.tabs) ? tabSettings.tabs : [];
      const tabSwitcherClass = tabSettings.tabSwitcherClass || "mb-6";
      const listContainerClass = tabSettings.listContainerClass || "";
      const itemWrapperClass = tabSettings.itemWrapperClass || "";
      const displayLimit = tabSettings.displayLimit;

      // displayLimit varsa ilk N item'ı göster
      const shouldShowButton = displayLimit && filteredItems.length > displayLimit;
      const displayedItems = displayLimit && !showAll
        ? filteredItems.slice(0, displayLimit)
        : filteredItems;

      // Card'lara geçilecek ekstra prop'lar
      let cardAdditionalProps = section.cardAdditionalProps || {};

      return (
        <>
          <LayoutWrapper
            title={title}
            description={description}
            link={link}
            settings={resolvedSection.settings}
          >
            {uiTabs.length > 1 && (
              <TabSwitcher
                tabs={uiTabs}
                selected={activeTab}
                onChange={setActiveTab}
                className={tabSwitcherClass}
              />
            )}
            <div className={clsx("tab-list-container", listContainerClass)}>
              {displayedItems.map((item, index) => {
                const dataLayerPropName = section.dataLayer?.itemPropName || "";
                const dataLayerItem = dataLayerPropName ? item[dataLayerPropName] : null;

                const key = getStableItemKey(item, index, section.id);
                const props = itemPropName
                  ? {
                    [itemPropName]: item,
                    ...cardAdditionalProps,
                  }
                  : {
                    ...item,
                    ...cardAdditionalProps,
                  };

                  if (dataLayerItem && dataLayerPropName != "") {
                    props[dataLayerPropName] = dataLayerItem;
                  }

                return (
                  <div key={key} className={clsx(itemWrapperClass)}>
                    <Card {...props} />
                  </div>
                );
              })}
            </div>
            {shouldShowButton && (
              <ShowAllButton
                isExpanded={showAll}
                onToggle={() => setShowAll(!showAll)}
              />
            )}
          </LayoutWrapper>

          {/* Debug Data Display */}
          <DebugDataDisplay
            sectionId={id}
            sectionType={type}
            data={data}
            source={section.source}
            additionalInfo={{
              displayType: section.displayType,
              cardComponent: section.cardComponent,
              itemPropName,
              cardAdditionalProps,
              tabSettings,
              activeTab,
              filteredItemsCount: filteredItems.length,
              uiTabsCount: uiTabs.length,
              tabSwitcherClass,
              listContainerClass,
              itemWrapperClass,
              priority: priority,
              locale,
              currency,
              additionalParams,
            }}
          />
        </>
      );
    }

    // Card bulunamazsa hiç render etme
    return null;
  }

  // displayType: "tab-swiper" → tab filtreleme + swiper render
  if (
    section.displayType === DISPLAY_TYPES.TAB_SWIPER &&
    section.cardComponent &&
    (Array.isArray(data) || (data && typeof data === "object"))
  ) {
    if (hideEmptySections && filteredItems.length === 0) return null;
    const Card = COMPONENT_REGISTRY[section.cardComponent];
    const itemPropName = ITEM_PROP_MAPPING[section.cardComponent] || "item";

    if (Card) {
      const tabSettings = resolvedSection.settings?.tab || {};
      const tabs = Array.isArray(tabSettings.tabs) ? tabSettings.tabs : [];
      const tabSwitcherClass = tabSettings.tabSwitcherClass || "mb-6";

      // Swiper ayarları (mevcut swiper tipindeki ile aynı pattern)
      const defaultSwiperOptions = {
        slidesPerView: "auto",
      };
      const swiperSettings = resolvedSection.settings?.swiper || {};
      const swiperOptions = {
        ...defaultSwiperOptions,
        ...swiperSettings.options,
      };

      // Tab filtresinden gelen item'lar
      const itemsForSwiper = filteredItems;

      // Card'lara geçilecek ekstra prop'lar
      const cardAdditionalProps = section.cardAdditionalProps || {};

      return (
        <>
          <LayoutWrapper
            title={title}
            description={description}
            link={link}
            settings={resolvedSection.settings}
          >
            {uiTabs.length > 1 && (
              <TabSwitcher
                tabs={uiTabs}
                selected={activeTab}
                onChange={setActiveTab}
                className={tabSwitcherClass}
              />
            )}
            <Swiper
              customClass={swiperSettings.customClass}
              swiperOptions={swiperOptions}
            >
              {itemsForSwiper.map((item, index) => {
                const key = getStableItemKey(item, index, section.id);
                const dataLayerPropName = section.dataLayer?.itemPropName || "";
                const dataLayerItem = dataLayerPropName ? item[dataLayerPropName] : null;
                const props = itemPropName
                  ? {
                    [itemPropName]: item,
                    ...cardAdditionalProps,
                  }
                  : {
                    ...item,
                    ...cardAdditionalProps,
                  };
                if (dataLayerItem && dataLayerPropName !== "") {
                  props[dataLayerPropName] = dataLayerItem;
                }
                return (
                  <SwiperSlide
                    key={key}
                    className={clsx(
                      swiperSettings.slideClass,
                      index > 0 && swiperSettings.spacingClass,
                    )}
                  >
                    <Card {...props} />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </LayoutWrapper>

          {/* Debug Data Display */}
          <DebugDataDisplay
            sectionId={id}
            sectionType={type}
            data={data}
            source={section.source}
            additionalInfo={{
              displayType: section.displayType,
              cardComponent: section.cardComponent,
              itemPropName,
              cardAdditionalProps,
              tabSettings,
              uiTabsCount: uiTabs.length,
              activeTab,
              filteredItemsCount: filteredItems.length,
              swiperSettings,
              swiperOptions,
              priority: priority,
              locale,
              currency,
              additionalParams,
            }}
          />
        </>
      );
    }

    // Card bulunamazsa hiç render etme
    return null;
  }

  // Bilinmeyen displayType veya eşleşmeyen section - data yoksa ve hideEmptySections açıksa gösterme
  if (hideEmptySections && data == null) return null;

  return (
    <>
      <div className="rounded-lg border border-gray-300 bg-gray-50 p-4">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          {description && <p className="mt-1 text-gray-600">{description}</p>}
          {link && (
            <p className="mt-1 text-blue-600">
              Link:{" "}
              <a href={link.href} className="underline">
                {link.text}
              </a>
            </p>
          )}
          <div className="mt-2 text-sm text-gray-500">
            <span className="rounded bg-gray-200 px-2 py-1">ID: {id}</span>
            <span className="ml-2 rounded bg-gray-200 px-2 py-1">
              Type: {type}
            </span>
            <span className="ml-2 rounded bg-gray-200 px-2 py-1">
              Priority: {priority}
            </span>
          </div>
        </div>

        <div className="rounded border bg-white p-4">
          <h3 className="mb-2 text-lg font-semibold">Data:</h3>
          <pre className="max-h-96 overflow-auto rounded bg-gray-100 p-3 text-sm">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>

      {/* Debug Data Display - URL'de debugmode=1 varsa gösterilir */}
      <DebugDataDisplay
        sectionId={id}
        sectionType={type}
        data={data}
        source={section.source}
        additionalInfo={{
          displayType: section.displayType,
          cardComponent: section.cardComponent,
          priority: priority,
          hasSettings: !!section.settings,
          locale,
          currency,
          additionalParams,
        }}
      />
    </>
  );
}

// Renderer functions removed - using DataDisplaySection instead

/**
 * hasMeaningfulData
 * Component için data'nın anlamlı olup olmadığını kontrol eder
 * requiresData: false olan section'larda bu kontrol yapılmaz
 */
function hasMeaningfulData(data) {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") {
    const values = Object.values(data);
    const arrays = values.filter(Array.isArray);
    if (arrays.length > 0) {
      return arrays.some((arr) => arr.length > 0);
    }
    return Object.keys(data).length > 0;
  }
  return true;
}

/**
 * getSectionData
 * Section'ın data source'una göre veriyi getirir
 * Client-side'da sadece mevcut data'yı döndürür
 */
function getSectionData(source, params) {
  if (!source) return null;

  // Static data
  if (source.type === "static") {
    return source.data;
  }

  // Server-side data (zaten fetch edilmiş)
  // Client-side'da sadece mevcut data'yı döndür
  if (source.data !== undefined) {
    return source.data;
  }

  // Eğer source.func varsa ama data yoksa, bu bir hata
  // Client-side'da server fonksiyonları çalıştıramayız
  if (source.func) {
    console.warn(
      "PageBuilder: Server-side function detected in client component. Data should be pre-fetched on server.",
    );
    return source.fallbackValue || null;
  }

  // Eğer data yoksa null döndür
  return null;
}

// Stabil key üretici: item'da id/slug/url varsa onu kullanır, yoksa section + index
function getStableItemKey(item, index, sectionId) {
  if (!item) return `${sectionId}-empty-${index}`;
  const candidate =
    item.id ||
    item.slug ||
    item.code ||
    item.key ||
    item.uuid ||
    item.Url ||
    item.URL ||
    item.url ||
    item.Name ||
    item.name;
  if (
    candidate &&
    (typeof candidate === "string" || typeof candidate === "number")
  ) {
    return `${sectionId}-${String(candidate)}`;
  }
  return `${sectionId}-${index}`;
}

// Server-side functions moved to src/lib/pageBuilderServer.js
// (No local normalization for FAQ; async layer prepares Accordion format.)
